import { config } from "@/config/env";
import { loginUser } from "modules/user";
import { useNavigate } from "react-router-dom";
import { isAxiosError } from "axios";
import { Input } from "@/components/ui/tremor/Input";
import { Button } from "@/components/ui/tremor/Button";
import { useEffect, useRef, useState } from "react";
import useAppDispatch from "../utils/hooks/useAppDispatch";
import { setIsAuthenticated } from "modules/user/userSlice";
import type { FC, ChangeEvent, SyntheticEvent } from "react";
import { LocalStorageItems } from "utils/hooks/localStorage";
import toast from "react-hot-toast";
import AnimatedLogo from "./svg/AnimatedLogo";
import SpinnerAnimation from "./svg/SpinnerAnimation";

const DEFAULT_LOGIN_ERROR =
  "Sign-in failed. Check your connection and try again.";

function getLoginFailureMessage(error: unknown): string {
  if (!isAxiosError<{ message?: string }>(error)) return DEFAULT_LOGIN_ERROR;

  const { message } = error.response?.data ?? {};
  if (typeof message !== "string" || message.trim() === "")
    return DEFAULT_LOGIN_ERROR;

  return message;
}

const Login: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const emailRef = useRef<HTMLInputElement | null>(null);
  const isMounted = useRef(false);

  const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const stored = localStorage.getItem(LocalStorageItems.DARKMODE);
  const isDarkMode = stored !== null ? stored === "true" : prefersDark;
  const logoColor = isDarkMode ? "#f8f9f1" : "#000000";

  const [formValues, setFormValues] = useState({
    email: "",
    password: "",
    submitted: false,
    rememberMe: false,
  });
  const [loginError, setLoginError] = useState<string | null>(null);

  const isEnabled =
    formValues.email.length > 0 &&
    formValues.password.length > 0 &&
    !formValues.submitted;

  const clearError = () => {
    if (loginError) setLoginError(null);
  };

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormValues((prev) => ({ ...prev, email: event.target.value }));
    clearError();
  };

  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormValues((prev) => ({ ...prev, password: event.target.value }));
    clearError();
  };

  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setFormValues((prev) => ({ ...prev, submitted: true }));
    setLoginError(null);

    const result = await loginUser(
      formValues.email.trim(),
      formValues.password,
      formValues.rememberMe,
    );

    if (!isMounted.current) return;

    if (!result.success) {
      const message = getLoginFailureMessage(result.error);
      setFormValues((prev) => ({ ...prev, submitted: false }));
      setLoginError(message);
      emailRef.current?.focus();
      toast.error(message, {
        icon: "🚨",
        position: "top-center",
        duration: 4000,
        className: "text-sm poppins",
      });
    } else {
      dispatch(setIsAuthenticated({ isAuthenticated: true }));
      navigate("/dashboard", { replace: true });
    }
  };

  useEffect(() => {
    isMounted.current = true;
    emailRef.current?.focus();
    return () => {
      isMounted.current = false;
    };
  }, []);

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-2 w-full">
      <div className="hidden lg:flex flex-col items-center justify-center scroll-sims-section bg-primary px-12">
        <div className="max-w-md text-center">
          <div className="flex w-40 h-40 mx-auto mb-8">
            <AnimatedLogo color={isDarkMode ? "#f8f9f1" : "#ffffff"} />
          </div>
          <h2 className="text-3xl font-bold brand-text text-gray-50 dark:text-text-primary w-full">
            aboutGOLF Clubhouse
          </h2>
          <p className="mt-3 text-sm text-gray-50 dark:text-text-primary mulish leading-relaxed">
            The complete management platform for indoor golf facilities.
          </p>
        </div>
      </div>
      <div className="flex flex-col items-center justify-center px-8 py-12 sm:px-12 bg-background">
        <div className="w-full max-w-sm">
          <div className="flex justify-center mb-6 sm:hidden">
            <div
              className={`w-20 h-20 ${formValues.submitted ? "hidden" : ""}`}
              aria-hidden={formValues.submitted}
            >
              <AnimatedLogo color={logoColor} />
            </div>
            <div
              className={`w-20 h-20 ${formValues.submitted ? "" : "hidden"}`}
              aria-hidden={!formValues.submitted}
            >
              <SpinnerAnimation color={logoColor} />
            </div>
          </div>

          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold text-text-primary brand-heading mb-3">
              Login to your account
            </h1>
            <p className="mt-1 text-sm text-text-muted mulish leading-relaxed">
              Enter your email below to login to your Clubhouse account
            </p>
          </div>

          <form
            onSubmit={(e) => void handleSubmit(e)}
            className="space-y-4"
            aria-busy={formValues.submitted}
          >
            {loginError && (
              <div
                role="alert"
                aria-live="polite"
                className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-400"
              >
                {loginError}
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="email"
                className="text-sm font-medium text-text-primary poppins"
              >
                Email
              </label>
              <Input
                type="email"
                id="email"
                name="email"
                ref={emailRef}
                autoComplete="email"
                placeholder="email@company.com"
                value={formValues.email}
                onChange={handleEmailChange}
                maxLength={254}
                required
                aria-invalid={loginError ? true : undefined}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="password"
                  className="text-sm font-medium text-text-primary poppins"
                >
                  Password
                </label>
                <a
                  href={config.pwReset}
                  className="text-xs mulish text-text-muted hover:text-primary transition-colors"
                >
                  Forgot password?
                </a>
              </div>
              <Input
                type="password"
                id="password"
                name="password"
                autoComplete="current-password"
                onChange={handlePasswordChange}
                maxLength={128}
                required
                aria-invalid={loginError ? true : undefined}
              />
            </div>

            <Button
              type="submit"
              disabled={!isEnabled}
              isLoading={formValues.submitted}
              loadingText="Signing in..."
              className="mt-2 w-full poppins bg-brandGreen hover:bg-brandDarkGreen dark:bg-brandGreen dark:hover:bg-brandDarkGreen text-white dark:text-white disabled:bg-brandGreen/50 disabled:dark:bg-brandGreen/30 disabled:text-white/60 disabled:dark:text-white/40"
            >
              Sign in
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
